import Card from "components/card";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";
import { useViewMode } from "contexts/ViewModeContext";
import DealsMap from "../dealmap/DealMap";

const DealMapCard = () => {
  const { viewMode, isTransitioning } = useViewMode();

  // Deal data
  const {
    consolidatedData,
    currentDealName,
    currentGroup,
    competitionData
  } = useDealData();

  // Portfolio data
  const {
    consolidatedPortfolioData,
    currentPortfolioName,
    portfolioCompetitionData,
    currentGroup: currentPortfolioGroup,
  } = usePortfolioData();

  // Use appropriate data based on view mode
  const mapData = viewMode === 'portfolio' ? consolidatedPortfolioData : consolidatedData;
  const currentName = viewMode === 'portfolio' ? currentPortfolioName : currentDealName;

  // For portfolio mode, convert to lowercase for data access (keys are lowercase)
  // but keep sentence case for display (currentPortfolioName is already in sentence case)
  const portfolioKey = viewMode === 'portfolio' && currentName
    ? currentName.toLowerCase()
    : currentName;
  const portfolioDisplayName = viewMode === 'portfolio' && currentName
    ? currentName  // Already in sentence case from PortfolioContext
    : currentName;

  // Build portfolio facilities from consolidatedPortfolioData for both modes.
  // In deal mode this powers the "Portfolio Facilities" secondary marker layer.
  // In portfolio mode it is also used, so both paths share the same data source.
  const portfolioFacilitiesFromContext = consolidatedPortfolioData
    ? {
      portfolioProperties: Object.entries(consolidatedPortfolioData)
        .flatMap(([key, portfolioData]) =>
          portfolioData?.facilities
            ? Object.values(portfolioData.facilities)
              .filter(facility => facility?.details?.Facilities !== "Consolidated")
              .map(facility => ({
                facilityName: facility.details.Facilities,
                city: facility.details["City/Town"] || facility.details.city,
                state: facility.details.State || facility.details.state,
                details: {
                  ...facility.details,
                  operator: facility.details.Operator || facility.details.operator,
                  Deal: key
                },
                coordinates: facility.details.Coordinates
                  ? (typeof facility.details.Coordinates === 'string'
                    ? JSON.parse(facility.details.Coordinates)
                    : facility.details.Coordinates)
                  : facility.details.coordinates
              }))
            : []
        )
    }
    : { portfolioProperties: [] };

  const portfolioFacilities = portfolioFacilitiesFromContext;

  // Don't render during transitions to prevent Leaflet errors
  if (isTransitioning) {
    return (
      <Card extra="justify-around w-full p-5 bg-cover gap-2 flex flex-col relative">
        <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">
          {viewMode === 'portfolio' ? 'Portfolio Map' : 'Deal Map'}
        </h3>
        <div className="h-96 w-full flex items-center justify-center">
          <div className="text-gray-500">Loading map...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="justify-around w-full p-5 bg-cover gap-2 flex flex-col relative">
      <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">
        {viewMode === 'portfolio' ? 'Portfolio Map' : 'Deal Map'}
      </h3>
      <div className="h-96 w-full sm:px-7 flex justify-center relative !z-0">
        <DealsMap
          key={`dealsmap-${viewMode}`}
          consolidatedData={mapData}
          currentDealName={viewMode === 'portfolio' ? portfolioKey : currentName}
          portfolioFacilities={portfolioFacilities}
          currentGroup={viewMode === 'portfolio'
            ? (currentPortfolioGroup ? { ...currentPortfolioGroup, dealId: currentPortfolioGroup.portfolioId } : null)
            : currentGroup}
          competitionData={competitionData}
          portfolioCompetitionData={portfolioCompetitionData}
          isPortfolioMode={viewMode === 'portfolio'}
        />
      </div>
    </Card>
  );
};

export default DealMapCard;